import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
    applyColgroupWidths,
    convertFileToBase64,
    Indent,
    LANGUAGES,
    LineHeight,
    OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan,
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IRichText } from '@modeso/types__aumico-lib-notes-be';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import Debug from 'debug';
import { NotesOptionsBase } from '../NotesOptionsBase';
import { IRichTextDelegate } from './IRichTextDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:RichTextComponent');
@Component({
    selector: 'aumico-notes-rich-text',
    templateUrl: 'richText.component.html',
    styleUrls: ['richText.component.scss'],
})
export class RichTextComponent extends NotesOptionsBase implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    richTextOfActiveLanguage: IRichText = {
        language: LANGUAGES.SWISS_GERMAN,
        text: '',
        title: '',
    } as IRichText;
    restOfTextWithLanguages: IRichText[] = [];

    @Input() translatableTexts: IRichText[] = [];

    @Input() disableRichText = false;

    @Input() language: string = LANGUAGES.SWISS_GERMAN;

    @Input() componentIndex: number;

    @Input() hideReference = false;

    DELEGATE?: IRichTextDelegate;
    currentLanguageText: string;

    activeLanguageRichTextIndex: number;

    @Input() public set delegate(delegate: IRichTextDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    @Input() asDialog = false;
    @Input() viewMode: ViewMode = ViewMode.EDIT;

    form: UntypedFormGroup;

    public tiptapEditor: Editor | null = null;
    public tiptapValue = '';
    @ViewChild('tiptapHost') tiptapHost?: ElementRef<HTMLDivElement>;
    @ViewChild('tiptapImageInput') tiptapImageInput?: ElementRef<HTMLInputElement>;
    public isParagraphMenuOpen = false;
    public isAlignMenuOpen = false;
    public isLineHeightMenuOpen = false;
    public isTableMenuOpen = false;
    public isImageSizeMenuOpen = false;
    public isCellAlignMenuOpen = false;
    public isCellVerticalMenuOpen = false;

    constructor(private dialogRef: MatDialogRef<RichTextComponent>) {
        super();
        dialogRef.disableClose = true;
    }

    ngOnInit(): void {
        debug('ngOnInit');
        debug(this.translatableTexts);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['translatableTexts']?.currentValue ||
            changes['richTextOfActiveLanguage']?.currentValue ||
            (changes['language']?.currentValue && this.language)
        ) {
            this.prepareTextPerLanguageToDisplay();
            this.initRichText();
        }
    }

    ngOnDestroy(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    ngAfterViewInit(): void {
        this.ensureTiptapEditor();
    }

    private prepareTextPerLanguageToDisplay() {
        this.activeLanguageRichTextIndex = this.translatableTexts.findIndex(
            (text: IRichText) => text.language === this.language,
        );

        this.richTextOfActiveLanguage = this.translatableTexts[this.activeLanguageRichTextIndex];
    }

    initRichText() {
        this.form = new FormGroup({
            [this.richTextOfActiveLanguage?.language || 'de-ch']: new FormControl<string>(
                this.richTextOfActiveLanguage?.text,
            ),
        });

        this.syncTiptapContent();
        this.richTextContents();
    }

    richTextContents() {
        if (!this.form.valid) {
            return;
        }

        if (this.DELEGATE) {
            const output: IRichText = this.form.value;
            const indexOfActiveLanguage = this.translatableTexts.findIndex(
                (text: IRichText) => text.language === this.language,
            );
            this.translatableTexts[indexOfActiveLanguage] = {
                language: this.language,
                text: output[this.language],
            };
            this.DELEGATE.richTextContent(this.translatableTexts, this.componentIndex);
        }
    }

    closeDialog() {
        this.richTextContents();
        this.dialogRef.close();
    }

    private ensureTiptapEditor(): void {
        if (this.tiptapEditor || !this.tiptapHost?.nativeElement) {
            return;
        }

        const extensions: AnyExtension[] = [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];

        this.tiptapEditor = new Editor({
            element: this.tiptapHost.nativeElement,
            extensions,
            content: this.tiptapValue,
            onUpdate: ({ editor }) => {
                this.handleTiptapUpdate(editor.getHTML());
            },
        });
    }

    private syncTiptapContent(): void {
        const rawText = this.richTextOfActiveLanguage?.text ?? '';
        const normalizedText = this.normalizeRichTextHtml(rawText);
        if (this.richTextOfActiveLanguage && normalizedText !== rawText) {
            this.richTextOfActiveLanguage.text = normalizedText;
        }
        this.tiptapValue = applyColgroupWidths(normalizedText);
        this.ensureTiptapEditor();
        if (this.tiptapEditor) {
            this.tiptapEditor.commands.setContent(this.tiptapValue, false);
        }
    }

    private handleTiptapUpdate(html: string): void {
        const normalizedHtml = this.normalizeRichTextHtml(html);
        this.tiptapValue = normalizedHtml;
        const control = this.form?.get(this.language);
        if (control) {
            control.setValue(normalizedHtml, { emitEvent: false });
        }
        this.currentLanguageText = normalizedHtml;
        this.richTextContents();
        if (this.richTextOfActiveLanguage) {
            this.richTextOfActiveLanguage.text = normalizedHtml;
        }
    }

    private normalizeRichTextHtml(html: string): string {
        if (!html) {
            return html;
        }
        let normalized = html.replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p>&nbsp;</p>');
        normalized = normalized.replace(/<p><br\s*\/?>\s*<\/p>/gi, '<p>&nbsp;</p>');
        return normalized;
    }

    tiptapUndo(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.undo?.();
    }

    tiptapRedo(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.redo?.();
    }

    tiptapToggleBold(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBold?.();
    }

    tiptapToggleItalic(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleItalic?.();
    }

    tiptapToggleStrike(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleStrike?.();
    }

    tiptapRemoveFormat(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearNodes?.();
        (this.tiptapEditor as any)?.commands?.unsetAllMarks?.();
    }

    tiptapToggleBlockquote(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBlockquote?.();
    }

    tiptapToggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6 = 2): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeading?.({ level });
    }

    tiptapToggleBulletList(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBulletList?.();
    }

    tiptapToggleOrderedList(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleOrderedList?.();
    }

    tiptapInsertPageBreak(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertPageBreak?.();
    }

    tiptapInsertTabSpan(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertTabSpan?.();
    }

    tiptapToggleUnderline(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleUnderline?.();
    }

    tiptapSetAlign(value: 'left' | 'center' | 'right' | 'justify'): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTextAlign?.(value);
    }

    tiptapSetLineHeight(value: number): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setLineHeight?.(value);
    }

    tiptapUnsetLineHeight(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.unsetLineHeight?.();
    }

    tiptapIndent(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.indent?.();
    }

    tiptapOutdent(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.outdent?.();
    }

    tiptapSelectAll(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.selectAll?.();
    }

    tiptapInsertImage(): void {
        this.tiptapFocus();
        this.tiptapImageInput?.nativeElement?.click();
    }

    tiptapToggleImageSizeMenu(): void {
        this.isImageSizeMenuOpen = !this.isImageSizeMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapSetImageSize(size: 'auto' | '25' | '50' | '75' | '100'): void {
        if (!this.tiptapEditor || !(this.tiptapEditor as any).isActive?.('image')) {
            return;
        }

        const width = size === 'auto' ? null : `${size}%`;
        const style = size === 'auto' ? null : `width: ${size}%; height: auto;`;

        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.updateAttributes?.('image', {
            width,
            height: null,
            style,
        });
        this.isImageSizeMenuOpen = false;
    }

    async onTiptapImageSelected(file: File): Promise<void> {
        if (!file?.type?.startsWith('image/')) {
            return;
        }

        try {
            const base64 = await convertFileToBase64(file);
            if (!base64) {
                return;
            }
            this.tiptapFocus();
            (this.tiptapEditor as any)?.commands?.setImage?.({ src: base64 });
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    }

    toggleParagraphMenu(): void {
        this.isParagraphMenuOpen = !this.isParagraphMenuOpen;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleAlignMenu(): void {
        this.isAlignMenuOpen = !this.isAlignMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleLineHeightMenu(): void {
        this.isLineHeightMenuOpen = !this.isLineHeightMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleTableMenu(): void {
        this.isTableMenuOpen = !this.isTableMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapToggleCellAlignMenu(): void {
        this.isCellAlignMenuOpen = !this.isCellAlignMenuOpen;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    tiptapToggleCellVerticalMenu(): void {
        this.isCellVerticalMenuOpen = !this.isCellVerticalMenuOpen;
        this.isCellAlignMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    tiptapSetCellAlign(value: 'left' | 'center' | 'right' | null): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTableCellTextAlign?.(value);
        this.isCellAlignMenuOpen = false;
    }

    tiptapSetCellVerticalAlign(value: 'top' | 'middle' | 'bottom' | null): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTableCellVerticalAlign?.(value);
        this.isCellVerticalMenuOpen = false;
    }

    closeMenus(): void {
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapIsInTable(): boolean {
        return !!(this.tiptapEditor && (this.tiptapEditor as any).isActive?.('table'));
    }


    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) {
                return;
            }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) {
                    button.classList.add('is-selected');
                }
                button.textContent = item;
                button.addEventListener('click', () => {
                    currentCommand?.({ id: item, label: item });
                });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) {
                return;
            }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) {
                        return false;
                    }
                    if (currentItems.length === 0) {
                        return false;
                    }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') {
                        return true;
                    }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }
    tiptapInsertTable(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertTable?.({ rows: 3, cols: 3, withHeaderRow: true });
    }

    tiptapAddColumnBefore(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addColumnBefore?.();
    }

    tiptapAddColumnAfter(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addColumnAfter?.();
    }

    tiptapDeleteColumn(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteColumn?.();
    }

    tiptapAddRowBefore(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addRowBefore?.();
    }

    tiptapAddRowAfter(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addRowAfter?.();
    }

    tiptapDeleteRow(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteRow?.();
    }

    tiptapDeleteTable(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteTable?.();
    }

    tiptapMergeCells(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.mergeCells?.();
    }

    tiptapSplitCell(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.splitCell?.();
    }

    tiptapToggleHeaderRow(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeaderRow?.();
    }

    tiptapToggleHeaderColumn(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeaderColumn?.();
    }

    tiptapColumnTotal(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-Total');
    }

    tiptapColumnHighlight(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-Highlight');
    }

    tiptapColumnNumber(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-number');
    }

    tiptapClearColumnStyles(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearColumnStyles?.();
    }

    tiptapRowTotal(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setRowStyle?.('row-Total');
    }

    tiptapClearRowStyles(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearRowStyles?.();
    }

    private tiptapFocus(): void {
        (this.tiptapEditor as any)?.commands?.focus?.();
    }
}
